// Public Client Document Upload Page (no auth required)
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Scale, 
  Upload, 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  X,
  Clock,
  Shield,
  File
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientUploadPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [uploadInfo, setUploadInfo] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchUploadInfo();
    fetchUploadedDocuments();
  }, [token]);

  const fetchUploadInfo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/client-upload/${token}`);
      if (res.ok) {
        setUploadInfo(await res.json());
      } else {
        const data = await res.json();
        setError(data.detail || 'Invalid or expired upload link');
      }
    } catch (err) {
      setError('Failed to load upload portal');
    } finally {
      setLoading(false);
    }
  };

  const fetchUploadedDocuments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/client-upload/${token}/documents`);
      if (res.ok) {
        setUploadedFiles(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch uploaded documents:', err);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = uploadInfo?.allowed_types || [];
    
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        toast.error(`File type .${ext} is not allowed`);
        return false;
      }
      return true;
    });
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    const remainingSlots = uploadInfo.max_files - uploadInfo.files_uploaded;
    if (selectedFiles.length > remainingSlots) {
      toast.error(`You can only upload ${remainingSlots} more file(s)`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    let successCount = 0;
    const totalFiles = selectedFiles.length;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await fetch(`${API_URL}/api/intake/client-upload/${token}`, {
          method: 'POST',
          body: formData
        });

        if (res.ok) {
          successCount++;
          setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
        } else {
          const data = await res.json();
          toast.error(`Failed to upload ${file.name}: ${data.detail}`);
        }
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    setSelectedFiles([]);
    
    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} file(s)`);
      fetchUploadInfo();
      fetchUploadedDocuments();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-center">
          <Upload className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading upload portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Unable to Access Upload Portal</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const filesRemaining = uploadInfo.max_files - uploadInfo.files_uploaded;

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="client-upload-page">
      {/* Header */}
      <div className="max-w-3xl mx-auto px-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-navy flex items-center justify-center">
            <Scale className="w-6 h-6 text-white" />
          </div>
          <div>
            <span className="font-heading text-xl font-bold text-navy">Legal AI</span>
            <span className="font-heading text-xl font-bold text-bronze">Dynamics</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4">
        {/* Welcome Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-600" />
              Secure Document Upload
            </CardTitle>
            <CardDescription>
              {uploadInfo.firm_name && (
                <span className="block mb-1">
                  <strong>{uploadInfo.firm_name}</strong> has requested documents from you.
                </span>
              )}
              Hello <strong>{uploadInfo.client_name}</strong>, please upload your documents below.
              {uploadInfo.case_type && (
                <span className="block mt-1">
                  Case type: <Badge variant="outline">{uploadInfo.case_type}</Badge>
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Expires: {formatDate(uploadInfo.expires_at)}
              </div>
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4" />
                Secure & Encrypted
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Upload Documents</CardTitle>
            <CardDescription>
              <span className="font-medium text-foreground">{filesRemaining}</span> of {uploadInfo.max_files} uploads remaining
              <span className="block mt-1">
                Allowed file types: {uploadInfo.allowed_types.map(t => `.${t}`).join(', ')}
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filesRemaining > 0 ? (
              <>
                {/* Drop Zone */}
                <div 
                  className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center hover:border-emerald-400 transition-colors cursor-pointer mb-4"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                    accept={uploadInfo.allowed_types.map(t => `.${t}`).join(',')}
                  />
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="font-medium text-foreground mb-1">Click to select files</p>
                  <p className="text-sm text-muted-foreground">
                    or drag and drop your documents here
                  </p>
                </div>

                {/* Selected Files */}
                {selectedFiles.length > 0 && (
                  <div className="space-y-2 mb-4">
                    <Label>Selected Files ({selectedFiles.length})</Label>
                    {selectedFiles.map((file, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <File className="w-5 h-5 text-emerald-600" />
                          <div>
                            <p className="font-medium text-sm">{file.name}</p>
                            <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeSelectedFile(i)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Progress */}
                {uploading && (
                  <div className="mb-4">
                    <Progress value={uploadProgress} className="h-2" />
                    <p className="text-sm text-muted-foreground mt-2 text-center">
                      Uploading... {uploadProgress}%
                    </p>
                  </div>
                )}

                {/* Upload Button */}
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={uploadFiles}
                  disabled={selectedFiles.length === 0 || uploading}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload {selectedFiles.length > 0 ? `${selectedFiles.length} File(s)` : 'Files'}
                    </>
                  )}
                </Button>
              </>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <p className="font-medium">Maximum upload limit reached</p>
                <p className="text-sm text-muted-foreground">
                  You have uploaded the maximum number of files allowed.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Uploaded Files */}
        {uploadedFiles.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Your Uploaded Documents ({uploadedFiles.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {uploadedFiles.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-medium text-sm">{doc.filename}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(doc.file_size)} • Uploaded {formatDate(doc.created_at)}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-emerald-700 border-emerald-300">
                      {doc.file_type.toUpperCase()}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-muted-foreground">
          <p>Your documents are securely transmitted and stored.</p>
          <p className="mt-1">If you have questions, please contact the law firm directly.</p>
        </div>
      </div>
    </div>
  );
}
